import React, { useState } from 'react';
import { ampli } from '@/ampli';
import { useAppDispatch, useAppSelector } from '@/features';
import { Button } from '@/shared/components/ui/button';
import { CustomLink } from '@/shared/components/ui/custom-link';
import { Badge } from '@mui/material';
import Popover from '@mui/material/Popover';
import CircularProgress from 'components/CircularProgress';
import { closeNotifications, openNotifications } from 'features/Common';
import { quickNotificationsOpen } from 'features/selectors';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { Bell } from 'lucide-react';
import { isMobile } from 'react-device-detect';

import NotificationsContent from '../NotificationsContent';

const NotificationsQuickView: React.FC = () => {
  const { width } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const notifications = useAppSelector((state) => state.notifications.notifications);
  const notificationsOpen = useAppSelector(quickNotificationsOpen);
  const unseenNotificationsCount = useAppSelector((state) => state.notifications.unseenCount);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    ampli.notificationsBellSelected();
    setAnchorEl(event.currentTarget);
    dispatch(openNotifications());
  };

  const handleCloseNotifications = () => {
    dispatch(closeNotifications());
    setAnchorEl(null);
  };

  return notifications ? (
    <>
      <Button
        onClick={handleOpen}
        size="icon"
        variant="ghost"
        className="$tw-relative $tw-rounded-full $tw-border-none hover:$tw-bg-muted/20"
        autoFocus={false}
      >
        <Badge badgeContent={unseenNotificationsCount} color="error">
          <Bell />
        </Badge>
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={notificationsOpen}
        onClose={handleCloseNotifications}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: width <= 425 ? '100%' : 500,
            height: '80%',
            maxHeight: '748px',
            borderRadius: 2,
            scrollbarWidth: 0,
            position: 'relative',
            top: `${isMobile || width <= 560 ? 88 : 37}px !important`,
            overflow: 'hidden',
            margin: 0,
            transition: 'all 0.1s ease-in !important',
            '&::-webkit-scrollbar': { display: 'none' },
          },
        }}
      >
        <NotificationsContent size="small" closeNotifications={handleCloseNotifications} />
        <div className="$tw-absolute $tw-bottom-0 $tw-w-full $tw-p-2">
          <Button variant="ghost" className="$tw-w-full hover:$tw-text-foreground" asChild>
            <CustomLink to="/notifications" onClick={() => handleCloseNotifications()}>
              Ver todas las notificaciones
            </CustomLink>
          </Button>
        </div>
      </Popover>
    </>
  ) : (
    <CircularProgress />
  );
};

export default NotificationsQuickView;
